import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Calendar, Clock, MapPin, UserCog, Pencil, Users, Ban, Settings, History, CalendarOff, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ymd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const hm = (iso) => {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
const dateLabel = (iso) => new Date(iso).toLocaleDateString('de-CH', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
const dtLabel = (iso) => new Date(iso).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const ACTION_LABELS = {
  updated: 'Termin geändert',
  substitute: 'Trainervertretung',
  cancel_occurrence: 'Termin abgesagt',
  cancel_series: 'Kurs abgesagt',
  holiday_suspend: 'Feiertag ausgesetzt',
};

export default function CoursesAdminPage() {
  const [occurrences, setOccurrences] = useState([]);
  const [resources, setResources] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({ default: 0, warn_hours: 24, holiday: true });
  const [showAudit, setShowAudit] = useState(false);
  const [audit, setAudit] = useState([]);
  const [showHoliday, setShowHoliday] = useState(false);
  const [holidayPreview, setHolidayPreview] = useState({ enabled: true, affected: [], count: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const from = ymd(new Date());
      const [occRes, resRes, usrRes] = await Promise.all([
        axios.get(`${API}/courses/occurrences`, { params: { from_date: from } }),
        axios.get(`${API}/resources`),
        axios.get(`${API}/users`, { params: { role: 'trainer' } }).catch(() => ({ data: [] })),
      ]);
      const occ = Array.isArray(occRes.data) ? occRes.data : (occRes.data?.occurrences || []);
      const res = Array.isArray(resRes.data) ? resRes.data : (resRes.data?.resources || []);
      const usr = Array.isArray(usrRes.data) ? usrRes.data : (usrRes.data?.users || []);
      setOccurrences(occ);
      setResources(res);
      setTrainers(usr);
    } catch (e) {
      toast.error('Kurstermine konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (occ) => {
    const subId = occ.substitute_trainer_id || '';
    const subName = occ.substitute_trainer_name || '';
    setForm({
      date: ymd(new Date(occ.start_time)),
      start: hm(occ.start_time),
      end: hm(occ.end_time),
      resourceId: (occ.resource_ids && occ.resource_ids[0]) || '',
      mode: subId ? 'team' : (subName ? 'free' : 'none'),
      subId: subId || '',
      subFree: subId ? '' : subName,
      notify: true,
    });
    setEditing(occ);
  };

  const resourceName = (id) => resources.find((r) => r.id === id)?.name || id;

  const save = async () => {
    if (!form) return;
    const startLocal = new Date(`${form.date}T${form.start}:00`);
    const endLocal = new Date(`${form.date}T${form.end}:00`);
    if (!(endLocal > startLocal)) { toast.error('Ende muss nach Beginn liegen.'); return; }
    const body = {
      start_time: startLocal.toISOString(),
      end_time: endLocal.toISOString(),
      resource_ids: form.resourceId ? [form.resourceId] : [],
      notify: form.notify,
    };
    if (form.mode === 'none') body.clear_substitute = true;
    else if (form.mode === 'team' && form.subId) {
      const t = trainers.find((x) => x.id === form.subId);
      body.substitute_trainer_id = form.subId;
      body.substitute_trainer_name = t ? `${t.first_name || ''} ${t.last_name || ''}`.trim() : null;
    } else if (form.mode === 'free') {
      if (form.subFree.trim()) body.substitute_trainer_name = form.subFree.trim();
      else body.clear_substitute = true;
    }
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/courses/occurrences/${editing.id}`, body);
      const changes = data?.changes || [];
      toast.success(changes.length ? `Gespeichert · ${data.notified} Teilnehmer informiert` : 'Gespeichert (keine Änderung).');
      setEditing(null); setForm(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally { setSaving(false); }
  };

  const cancelOccurrence = async (occ) => {
    const reason = window.prompt(`Diesen Termin (${dateLabel(occ.start_time)} ${hm(occ.start_time)}) absagen? Gebuchte Teilnehmer werden informiert.\n\nGrund (optional, wird mitgesendet):`, '');
    if (reason === null) return;
    try {
      const { data } = await axios.post(`${API}/courses/occurrences/${occ.id}/cancel-occurrence`, { reason });
      toast.success(`Termin abgesagt · ${data.notified} Teilnehmer informiert`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Absage fehlgeschlagen'); }
  };

  const cancelSeries = async () => {
    if (!editing) return;
    const reason = window.prompt(`Den GANZEN Kurs "${editing.course_name}" absagen? Alle kommenden Termine werden abgesagt und alle Teilnehmer informiert.\n\nGrund (optional, wird mitgesendet):`, '');
    if (reason === null) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/courses/${editing.course_id}/cancel-series`, { reason });
      toast.success(`Kurs abgesagt · ${data.cancelled_occurrences} Termine · ${data.notified} Teilnehmer informiert`);
      setEditing(null); setForm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Absage fehlgeschlagen'); }
    finally { setSaving(false); }
  };

  const openSettings = async () => {
    try {
      const [mp, hs] = await Promise.all([
        axios.get(`${API}/courses/settings/min-participants`),
        axios.get(`${API}/courses/settings/holiday-suspend`),
      ]);
      setSettings({ default: mp.data.default ?? 0, warn_hours: mp.data.warn_hours ?? 24, holiday: hs.data.enabled !== false });
    } catch (e) { /* defaults */ }
    setShowSettings(true);
  };

  const saveSettings = async () => {
    try {
      await Promise.all([
        axios.post(`${API}/courses/settings/min-participants`, { default: Number(settings.default) || 0, warn_hours: Number(settings.warn_hours) || 24 }),
        axios.post(`${API}/courses/settings/holiday-suspend`, { enabled: !!settings.holiday }),
      ]);
      toast.success('Einstellungen gespeichert');
      setShowSettings(false);
    } catch (e) { toast.error('Speichern fehlgeschlagen'); }
  };

  const openHolidayPreview = async () => {
    try {
      const { data } = await axios.get(`${API}/courses/admin/holiday-preview`);
      setHolidayPreview({ enabled: data.enabled !== false, affected: data.affected || [], count: data.count || 0 });
    } catch (e) { setHolidayPreview({ enabled: true, affected: [], count: 0 }); }
    setShowHoliday(true);
  };

  const confirmApplyHolidays = async () => {
    try {
      const { data } = await axios.post(`${API}/courses/admin/apply-holidays`, {});
      if (!data.enabled) { toast.message('Feiertags-Aussetzung ist deaktiviert.'); }
      else toast.success(`Feiertage angewendet · ${data.suspended} ausgesetzt, ${data.unsuspended} reaktiviert`);
      setShowHoliday(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Fehlgeschlagen'); }
  };

  const openAudit = async () => {
    try {
      const { data } = await axios.get(`${API}/courses/audit-log`, { params: { limit: 100 } });
      setAudit(Array.isArray(data) ? data : []);
    } catch (e) { setAudit([]); }
    setShowAudit(true);
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6" data-testid="courses-admin-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-[#A9884A]" /> Kurse — Termine
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Termine verschieben, Trainervertretung setzen oder absagen. Gebuchte Teilnehmer werden automatisch per E-Mail, Push und WhatsApp informiert.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={openHolidayPreview} data-testid="apply-holidays-btn"><CalendarOff className="w-4 h-4 mr-1" /> Feiertage anwenden</Button>
          <Button variant="outline" size="sm" onClick={openAudit} data-testid="open-audit-btn"><History className="w-4 h-4 mr-1" /> Audit-Log</Button>
          <Button variant="outline" size="sm" onClick={openSettings} data-testid="open-settings-btn"><Settings className="w-4 h-4 mr-1" /> Einstellungen</Button>
        </div>
      </div>

      {loading ? (
        <div className="text-gray-400 text-sm py-10 text-center" data-testid="courses-loading">Lade Kurstermine…</div>
      ) : occurrences.length === 0 ? (
        <div className="text-gray-400 text-sm py-10 text-center" data-testid="courses-empty">Keine kommenden Kurstermine.</div>
      ) : (
        <div className="space-y-3">
          {occurrences.map((o) => (
            <Card key={o.id} data-testid={`course-occ-${o.id}`} className={`border-gray-200 ${o.holiday_suspended ? 'opacity-70' : ''}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 truncate flex items-center gap-2">
                    {o.course_name || 'Kurs'}
                    {o.holiday_suspended && (
                      <Badge variant="outline" className="border-amber-500 text-amber-600"><CalendarOff className="w-3 h-3 mr-1" />Fällt aus: {o.holiday_name || 'Feiertag'}</Badge>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 flex flex-wrap gap-x-4 gap-y-1 mt-1">
                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{dateLabel(o.start_time)}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{hm(o.start_time)}–{hm(o.end_time)}</span>
                    {o.resource_ids?.length ? (<span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{resourceName(o.resource_ids[0])}</span>) : null}
                    <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{o.booked_count || 0}/{o.capacity || 0}</span>
                  </div>
                  <div className="text-sm mt-1 flex items-center gap-1 text-gray-700">
                    <UserCog className="w-3.5 h-3.5" />
                    {o.substitute_trainer_name ? (
                      <Badge variant="outline" className="border-[#A9884A] text-[#A9884A]">Vertretung: {o.substitute_trainer_name}</Badge>
                    ) : (<span className="text-gray-500">{o.course_trainer_name || o.trainer_name || '—'}</span>)}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(o)} data-testid={`edit-occ-${o.id}`}><Pencil className="w-4 h-4 mr-1" /> Bearbeiten</Button>
                  <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => cancelOccurrence(o)} data-testid={`cancel-occ-${o.id}`}><Ban className="w-4 h-4 mr-1" /> Absagen</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) { setEditing(null); setForm(null); } }}>
        <DialogContent className="sm:max-w-md" data-testid="occ-edit-dialog">
          <DialogHeader>
            <DialogTitle>Termin bearbeiten</DialogTitle>
            <DialogDescription>Serie bleibt unberührt · Teilnehmer werden informiert</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <div><Label>Datum</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="occ-edit-date" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Beginn</Label><Input type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="occ-edit-start" /></div>
                <div><Label>Ende</Label><Input type="time" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} data-testid="occ-edit-end" /></div>
              </div>
              <div>
                <Label>Raum</Label>
                <Select value={form.resourceId} onValueChange={(v) => setForm({ ...form, resourceId: v })}>
                  <SelectTrigger data-testid="occ-edit-room"><SelectValue placeholder="Raum wählen" /></SelectTrigger>
                  <SelectContent>{resources.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Trainervertretung</Label>
                <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                  <SelectTrigger data-testid="occ-edit-sub-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Keine Vertretung</SelectItem>
                    <SelectItem value="team">Aus dem Team</SelectItem>
                    <SelectItem value="free">Freitext-Name</SelectItem>
                  </SelectContent>
                </Select>
                {form.mode === 'team' && (
                  <Select value={form.subId} onValueChange={(v) => setForm({ ...form, subId: v })}>
                    <SelectTrigger className="mt-2" data-testid="occ-edit-sub-trainer"><SelectValue placeholder="Trainer wählen" /></SelectTrigger>
                    <SelectContent>{trainers.map((t) => (<SelectItem key={t.id} value={t.id}>{`${t.first_name || ''} ${t.last_name || ''}`.trim() || t.email}</SelectItem>))}</SelectContent>
                  </Select>
                )}
                {form.mode === 'free' && (
                  <Input className="mt-2" placeholder="Name des Ersatztrainers" value={form.subFree} onChange={(e) => setForm({ ...form, subFree: e.target.value })} data-testid="occ-edit-sub-free" />
                )}
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div><div className="text-sm font-medium">Teilnehmer benachrichtigen</div><div className="text-xs text-gray-500">E-Mail · Push · WhatsApp bei Änderung</div></div>
                <Switch checked={form.notify} onCheckedChange={(v) => setForm({ ...form, notify: v })} data-testid="occ-edit-notify" />
              </div>
              <button type="button" onClick={cancelSeries} disabled={saving} className="w-full text-sm text-red-600 hover:underline flex items-center justify-center gap-1 pt-1" data-testid="occ-edit-cancel-series">
                <AlertTriangle className="w-4 h-4" /> Ganzen Kurs absagen
              </button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditing(null); setForm(null); }} data-testid="occ-edit-cancel">Abbrechen</Button>
            <Button onClick={save} disabled={saving} className="bg-[#A9884A] hover:bg-[#8f723d]" data-testid="occ-edit-save">{saving ? 'Speichern…' : 'Speichern & informieren'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-md" data-testid="courses-settings-dialog">
          <DialogHeader>
            <DialogTitle>Kurs-Einstellungen</DialogTitle>
            <DialogDescription>Mindestteilnehmer-Warnung & Feiertags-Aussetzung</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Mindestteilnehmer (Standard)</Label>
              <Input type="number" min="0" value={settings.default} onChange={(e) => setSettings({ ...settings, default: e.target.value })} data-testid="settings-min-default" />
              <p className="text-xs text-gray-500 mt-1">0 = keine Warnung. Pro Kurs überschreibbar.</p>
            </div>
            <div>
              <Label>Warn-Vorlaufzeit (Stunden)</Label>
              <Input type="number" min="1" value={settings.warn_hours} onChange={(e) => setSettings({ ...settings, warn_hours: e.target.value })} data-testid="settings-warn-hours" />
              <p className="text-xs text-gray-500 mt-1">Trainer wird gewarnt, sobald so viele Stunden vor Kursbeginn zu wenige Anmeldungen vorliegen.</p>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div><div className="text-sm font-medium">Feiertage/Ferien aussetzen</div><div className="text-xs text-gray-500">Termine an hinterlegten Feiertagen sind nicht buchbar. Dates verwaltest du unter Einstellungen → Feiertage.</div></div>
              <Switch checked={settings.holiday} onCheckedChange={(v) => setSettings({ ...settings, holiday: v })} data-testid="settings-holiday-toggle" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>Abbrechen</Button>
            <Button onClick={saveSettings} className="bg-[#A9884A] hover:bg-[#8f723d]" data-testid="settings-save">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Holiday preview dialog */}
      <Dialog open={showHoliday} onOpenChange={setShowHoliday}>
        <DialogContent className="sm:max-w-lg" data-testid="holiday-preview-dialog">
          <DialogHeader>
            <DialogTitle>Feiertage anwenden — Vorschau</DialogTitle>
            <DialogDescription>Diese kommenden Termine fallen auf Feiertage/Ferien und werden auf „nicht buchbar" gesetzt.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto space-y-2">
            {!holidayPreview.enabled ? (
              <div className="text-sm text-amber-600 py-4">Feiertags-Aussetzung ist in den Einstellungen deaktiviert.</div>
            ) : holidayPreview.affected.length === 0 ? (
              <div className="text-sm text-gray-400 py-6 text-center">Keine kommenden Termine an Feiertagen/Ferien.</div>
            ) : holidayPreview.affected.map((a) => (
              <div key={a.occurrence_id} className="border rounded-lg p-3 text-sm" data-testid={`holiday-aff-${a.occurrence_id}`}>
                <div className="font-medium text-gray-900">{a.course_name || 'Kurs'} · {dtLabel(a.start_time)}</div>
                <div className="text-gray-600 flex items-center gap-2 mt-0.5">
                  <Badge variant="outline" className="border-amber-500 text-amber-600">{a.holiday_name}</Badge>
                  <span>{a.bookings} Buchung(en)</span>
                  {a.already_suspended && <span className="text-xs text-gray-400">· bereits ausgesetzt</span>}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHoliday(false)}>Schliessen</Button>
            {holidayPreview.enabled && (
              <Button onClick={confirmApplyHolidays} className="bg-[#A9884A] hover:bg-[#8f723d]" data-testid="holiday-apply-confirm">Anwenden</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit log dialog */}
      <Dialog open={showAudit} onOpenChange={setShowAudit}>
        <DialogContent className="sm:max-w-lg" data-testid="courses-audit-dialog">
          <DialogHeader>
            <DialogTitle>Audit-Log</DialogTitle>
            <DialogDescription>Wer hat wann was geändert/abgesagt</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {audit.length === 0 ? (
              <div className="text-sm text-gray-400 py-6 text-center">Noch keine Einträge.</div>
            ) : audit.map((a) => (
              <div key={a.id} className="border rounded-lg p-3 text-sm" data-testid={`audit-${a.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900">{ACTION_LABELS[a.action] || a.action}</span>
                  <span className="text-xs text-gray-400">{a.created_at ? dtLabel(a.created_at) : ''}</span>
                </div>
                <div className="text-gray-700 mt-0.5">{a.course_name ? <span className="font-medium">{a.course_name}: </span> : null}{a.detail}</div>
                <div className="text-xs text-gray-400 mt-0.5">{a.actor_name || a.actor_id} ({a.actor_role || '—'})</div>
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowAudit(false)}>Schliessen</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
